import { useCallback, useMemo, useState } from "react";

/**
 * @param {string} value
 */
const upper = (value) => `${value}`.toUpperCase();

/**
 * Local-state variant of useAdvancedFilters that does NOT sync to URL search params.
 * Use this inside dialogs or other contexts where URL changes would cause unwanted remounts.
 *
 * @template T
 * @param {import("@seabrookstudios/pitch2table-core").AdvancedFilter<T>[]} advancedFilters
 * @returns {{
 *  selectionsByLabel: Record<string, string[]>,
 *  setSelectionsByLabel: (updater: import("react").SetStateAction<Record<string, string[]>>) => void
 * }}
 */
export const useLocalAdvancedFilters = (advancedFilters) => {
  const optionSetsByLabel = useMemo(() => {
    /** @type {Record<string, Set<string>>} */
    const map = {};
    for (const filter of advancedFilters) {
      map[filter.label] = new Set(filter.options.map(upper));
    }
    return map;
  }, [advancedFilters]);

  const [raw, setRaw] = useState(/** @type {Record<string, string[]>} */ ({}));

  const selectionsByLabel = useMemo(() => {
    /** @type {Record<string, string[]>} */
    const record = {};
    for (const filter of advancedFilters) {
      const allowed = optionSetsByLabel[filter.label] || new Set();
      const values = (raw[filter.label] || [])
        .map(upper)
        .filter((v) => v.length > 0)
        .filter((v) => allowed.has(v));
      record[filter.label] = Array.from(new Set(values));
    }
    return record;
  }, [advancedFilters, optionSetsByLabel, raw]);

  const setSelectionsByLabel = useCallback(
    (updater) => {
      setRaw((prev) => {
        const currentNormalized = {};
        for (const filter of advancedFilters) {
          const allowed = optionSetsByLabel[filter.label] || new Set();
          currentNormalized[filter.label] = (prev[filter.label] || [])
            .map(upper)
            .filter((v) => v.length > 0)
            .filter((v) => allowed.has(v));
        }

        const next = typeof updater === "function" ? updater(currentNormalized) : updater;

        /** @type {Record<string, string[]>} */
        const cleaned = {};
        for (const filter of advancedFilters) {
          const allowed = optionSetsByLabel[filter.label] || new Set();
          cleaned[filter.label] = Array.from(
            new Set(
              (next[filter.label] || [])
                .map(upper)
                .filter((v) => v.length > 0)
                .filter((v) => allowed.has(v))
            )
          );
        }
        return cleaned;
      });
    },
    [advancedFilters, optionSetsByLabel]
  );

  return { selectionsByLabel, setSelectionsByLabel };
};
